import React from 'react';
import styled from '@emotion/styled/macro';
import moment from 'moment';

import { TreeDeciduous } from 'images';
import { numberWithCommas } from 'utils';

import TimeIcon from './Icons/Time';
import TargetIcon from './Icons/TargetIcon';
import SupporterIcon from './Icons/SupporterIcon';
import DateIcon from './Icons/DateIcon';

export default function Counts(props) {
  const { project, projectSupporters, projectDays } = props;

  if (!project) return null;

  const isTreeProject = Object.prototype.hasOwnProperty.call(
    project,
    'tree_count'
  );

  const raisedAmount = Number(project.donations_sum || 0);
  const finalGoal = Number(project.final_goal || 0);

  const progressPercent =
    finalGoal > 0 ? Math.min((raisedAmount / finalGoal) * 100, 100) : 0;

  const formattedGoal = numberWithCommas(finalGoal.toFixed(2));

  const displayedEventDate = project.event_date || project.funding_end_date;
  const dateText = project.is_ticketable
    ? displayedEventDate
      ? moment.unix(Number(displayedEventDate)).format('DD.MM.YYYY')
      : '—'
    : `${projectDays || 0} ${projectDays > 1 ? 'Tage' : 'Tage'}`;

  return (
    <Wrapper>

      <StatsRow>
        {isTreeProject ? (
          <StatItem align="flex-start">
            <TreeIcon src={TreeDeciduous} alt="tree-deciduous-icon" />
            <StatContent>
              <MobileLabel>Bäume</MobileLabel>
              <StatText>{project.tree_count} Bäume</StatText>
            </StatContent>
          </StatItem>
        ) : (
          <StatItem align="flex-start">
            <IconWrap>
              <SupporterIcon />
            </IconWrap>
            <StatContent>
              <MobileLabel>
                {project.is_ticketable ? 'Besucher' : 'Unterstützer'}
              </MobileLabel>
              <StatText>
                {projectSupporters || 0}{' '}
                {project.is_ticketable ? 'Besucher' : 'Unterstützer'}
              </StatText>
              <MobileValue>{projectSupporters || 0}</MobileValue>
            </StatContent>
          </StatItem>
        )}

        {!project.is_ticketable && !project.is_patreon && (
          <StatItem align="center">
            <IconWrap>
              <TargetIcon />
            </IconWrap>
            <StatContent>
              <MobileLabel>Gesamtziel</MobileLabel>
              <StatText>Gesamtziel {formattedGoal} €</StatText>
              <MobileValue>{formattedGoal} €</MobileValue>
            </StatContent>
          </StatItem>
        )}

        <StatItem align="flex-end">
          <IconWrap>
            {project.is_ticketable ? <DateIcon /> : <TimeIcon />}
          </IconWrap>
          <StatContent>
            <MobileLabel>
              {project.is_ticketable ? 'Datum' : 'Restlaufzeit'}
            </MobileLabel>
            <StatText>
              {project.is_ticketable ? dateText : `${dateText} noch`}
            </StatText>
            <MobileValue>{dateText}</MobileValue>
          </StatContent>
        </StatItem>
      </StatsRow>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  gap: 18px;
  width: 100%
  max-width: 100%;
  height: 30px;

  margin-bottom: 24px;
  @media (max-width: 767px) {
    width: 100%;

    margin-bottom: 0px;
    height: 66px;
    align-self: stretch;
  }
`;


const StatsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 4px;
  width: 100%;
  max-width: 100%;
  height: 22px;
  margin-top:18px;

  @media (max-width: 767px) {
    margin-top: 18px;
    width: 100%;
    height: 36px;
    gap: 18px;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: ${({ align }) => align || 'center'};
  align-items: center;
  gap: 6px;
  height: 22px;
  flex: 0 0 auto;

  @media (max-width: 767px) {
    height: 36px;
    flex: 1;
    min-width: 0;
  }
`;

const IconWrap = styled.div`
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #656565;

  svg {
    width: 22px;
    height: 22px;
  }

  @media (max-width: 767px) {
    width: 24px;
    height: 24px;
    flex-basis: 24px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const TreeIcon = styled.img`
  width: 22px;
  height: 22px;
  flex: 0 0 22px;

  @media (max-width: 767px) {
    width: 24px;
    height: 24px;
    flex-basis: 24px;
  }
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

const StatText = styled.div`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #656565;
  white-space: nowrap;

  @media (max-width: 767px) {
    display: none;
  }
`;

const MobileLabel = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: block;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 18px;
    color: #656565;
    white-space: nowrap;
  }
`;

const MobileValue = styled.div`
  display: none;

  @media (max-width: 767px) {
    display: block;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 18px;
    color: #656565;
    white-space: nowrap;
  }
`;
